import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useBreadcrumbs } from '../layout/breadcrumbsContext/useBreadcrumbs/useBreadcrumbs'
import { TableProvider, Table, TableBody, TableRow, TableCell, TablePagination, Badge, Flex } from '@percent/lemonade'
import { useQueryList } from '@percent/cause-dashboard/common/hooks'
import { RoutePath } from '../routes/Routes'
import { useServices } from '@percent/cause-dashboard/context/serviceContext/ServiceContext'
import { useAuthState } from '@percent/cause-dashboard/common/hooks/useAuthState/useAuthState'
import { ActionsMenuVolunteering } from './actionsMenuVolunteering/ActionsMenuVolunteering'
import { VolunteeringActionsDialogsProvider } from './volunteeringActionsDialogs/VolunteeringActionsDialogsProvider'
import { getFormattedDate } from '@percent/cause-dashboard/common/utility/date'
import {
  CausesPortalOpportunityDto,
  OpportunityStatus,
  GetVolunteeringOrganisationCreatedOpportunitiesQuery
} from '@percent/cause-dashboard/services/volunteering/volunteeringService.types'
import { LinkButton } from '@percent/domain/giving'
import { useCanUseVolunteering } from '@percent/cause-dashboard/common/hooks/canUseVolunteering/canUseVolunteering'

export function Volunteering() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { volunteeringService } = useServices()
  const { authState } = useAuthState()
  const organisationId = authState.user?.organisationId
  const canUseVolunteering = useCanUseVolunteering()

  useEffect(() => {
    if (!canUseVolunteering.isPending && !canUseVolunteering.canUseVolunteering) {
      navigate(RoutePath.HOME)
    }
  }, [canUseVolunteering.isPending, canUseVolunteering.canUseVolunteering, navigate])

  const [{ data, totalResults, isLoading }, { nextPage, previousPage, refresh }] = useQueryList<
    GetVolunteeringOrganisationCreatedOpportunitiesQuery,
    CausesPortalOpportunityDto
  >(
    volunteeringService.getOrganisationCreatedOpportunities,
    {
      organisationId: organisationId as string,
      pageSize: 25,
      cursor: undefined
    },
    canUseVolunteering.canUseVolunteering
  )

  useBreadcrumbs([{ label: t('breadcrumb.volunteering') }])

  const getStatusBadge = (status: OpportunityStatus): JSX.Element => {
    switch (status) {
      case OpportunityStatus.ACTIVE:
        return <Badge variant="positive">{t('volunteering.opportunityStatus.active')}</Badge>
      case OpportunityStatus.CLOSED:
        return <Badge variant="default">{t('volunteering.opportunityStatus.past')}</Badge>
      case OpportunityStatus.CANCELLED:
        return <Badge variant="critical">{t('volunteering.opportunityStatus.cancelled')}</Badge>
      default:
        return <Badge variant="positive">{t('volunteering.opportunityStatus.active')}</Badge>
    }
  }

  const columns = useMemo(
    () => [
      { id: 'name', content: t('volunteering.table.header.name'), props: { width: '35%' } },
      { id: 'partner', content: t('volunteering.table.header.partner'), props: { width: '15%' } },
      { id: 'attendees', content: t('volunteering.table.header.attendees'), props: { width: '12%' } },
      { id: 'createdAt', content: t('volunteering.table.header.createdAt'), props: { width: '12%' } },
      { id: 'status', content: t('volunteering.table.header.status'), props: { width: '10%' } },
      { id: '', props: { width: '3%' } }
    ],
    [t]
  )

  return (
    <VolunteeringActionsDialogsProvider refresh={refresh}>
      <TableProvider
        columns={columns}
        totalResults={totalResults}
        isLoading={canUseVolunteering.isLoading || isLoading}
        illustration="no-results"
        emptyTableTitle={t('typography.volunteering.noVolunteeringOpportunitiesTitle')}
        emptyTableDescription={t('typography.volunteering.noVolunteeringOpportunitiesDescription')}
      >
        <Flex justify="end">
          <LinkButton to={RoutePath.VOLUNTEERING_CREATE_OPPORTUNITY} variant="primary" size="small">
            {t('volunteering.button.createOpportunity')}
          </LinkButton>
        </Flex>
        <Table>
          <TableBody>
            {data?.map(opportunity => (
              <TableRow key={opportunity.id}>
                <TableCell>{opportunity.name}</TableCell>
                <TableCell>{opportunity.partner.name}</TableCell>
                <TableCell>
                  {`${opportunity.participants.count}${
                    opportunity.participants.spots ? `/${opportunity.participants.spots}` : ''
                  }`}
                </TableCell>
                <TableCell>{getFormattedDate(opportunity.createdAt)}</TableCell>
                <TableCell>{getStatusBadge(opportunity.status)}</TableCell>
                <TableCell>
                  <ActionsMenuVolunteering opportunity={opportunity} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          totalResultsText={t('typography.results', {
            count: totalResults
          })}
          previousPage={previousPage}
          nextPage={nextPage}
        />
      </TableProvider>
    </VolunteeringActionsDialogsProvider>
  )
}
